import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AppContext';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * AuthCallback v100 — RECONSTRUCTION BLINDÉE
 * 
 * Flow:
 * 1. Emergent Auth redirects here with ?session_id=xxx or #session_id=xxx
 * 2. We exchange session_id for dual tokens (access + refresh)
 * 3. Redirect to dashboard. Done.
 * 
 * No PWA popup tricks. No Safari ITP hacks. No sessionStorage backup.
 * Simple = reliable.
 */
export const AuthCallback = () => {
  const { exchangeSession, updateUser } = useAuth();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('processing');
  const [errorMessage, setErrorMessage] = useState('');
  // Iter. 411.mm21 — expose a visible counter during retries so the user
  // doesn't see a frozen "Connexion en cours..." for 8+ seconds without
  // feedback while we backoff a stuttering Android network.
  const [retryHint, setRetryHint] = useState('');

  // Iter. 411.mm21 — Classify an axios error as "transient network"
  // (Android waking up from a fullscreen video, Wi-Fi handoff, DNS flap)
  // vs "deliberate auth failure" (401/403/404 from the server). Only the
  // transient class gets a retry — a real auth error short-circuits so we
  // don't hammer the backend with bad credentials.
  const isTransientNetworkError = (err) => {
    if (!err) return false;
    // ERR_NETWORK or ECONNABORTED (axios timeout) → no response at all
    if (!err.response) return true;
    const s = err.response.status;
    return s === 502 || s === 503 || s === 504;
  };

  // Retry wrapper: up to 2 additional attempts (3 total) with backoff
  // 1200ms → 2500ms. ~3.7s worst-case extra wait before we fall back to the
  // red error screen. Still well under the user's patience threshold on
  // mobile and matches the Android "network wake-up" window observed
  // after exiting fullscreen video (Simon, Pixel 9, 2026-02-09).
  const withRetry = async (fn, label) => {
    const delays = [1200, 2500]; // ms between tries
    let lastErr = null;
    for (let attempt = 0; attempt <= delays.length; attempt++) {
      try {
        if (attempt > 0) {
          setRetryHint(`Nouvelle tentative… (${attempt}/${delays.length})`);
          await new Promise((r) => setTimeout(r, delays[attempt - 1]));
        }
        return await fn();
      } catch (e) {
        lastErr = e;
        console.warn(`[AuthCallback] ${label} attempt ${attempt + 1} failed:`, e?.message);
        if (!isTransientNetworkError(e)) break; // real auth error — don't retry
      }
    }
    throw lastErr;
  };

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {

      // Extract session_id from EVERY possible location
      const hash = window.location.hash || '';
      const search = window.location.search || '';
      const hashParams = new URLSearchParams(hash.replace('#', ''));
      const queryParams = new URLSearchParams(search);

      // Also check sessionStorage for pre-React captured URL (Safari workaround)
      let preReactSessionId = null;
      try {
        const stored = sessionStorage.getItem('_auth_initial_url');
        if (stored) {
          const parsed = JSON.parse(stored);
          const preHash = new URLSearchParams((parsed.hash || '').replace('#', ''));
          const preSearch = new URLSearchParams((parsed.search || ''));
          preReactSessionId = preHash.get('session_id') || preSearch.get('session_id');
        }
      } catch (e) {
        // ignore
      }

      const sessionId = hashParams.get('session_id')
        || queryParams.get('session_id')
        || preReactSessionId;

      // Check for direct token in hash (PWA return)
      const tokenFromUrl = hashParams.get('token');

      if (tokenFromUrl) {
        localStorage.setItem('session_token', tokenFromUrl);
        try {
          const raw = axios.create();
          // Iter. 411.mm21 — retry wrap: ERR_NETWORK/timeout on Android
          // after exiting fullscreen video no longer dumps the user on the
          // red screen on first try.
          const res = await withRetry(() => raw.get(`${API}/auth/me`, {
            headers: { 'X-Session-Token': tokenFromUrl },
            timeout: 8000,
          }), '/auth/me');
          setRetryHint('');
          updateUser(res.data);
          const dest = getDestination(res.data);
          navigate(dest, { replace: true, state: { user: res.data, fromAuth: true } });
          return;
        } catch (e) {
          setRetryHint('');
        }
      }

      if (!sessionId) {
        setErrorMessage('Aucun identifiant de session. Retourne à l\'accueil et reconnecte-toi.');
        setStatus('error');
        return;
      }


      try {
        // Exchange session_id for tokens (stores in localStorage automatically)
        // Iter. 411.mm21 — same retry treatment for the OAuth exchange call.
        const user = await withRetry(() => exchangeSession(sessionId), '/auth/session');
        setRetryHint('');

        // Check if this was opened as a popup by PWA
        const isPopup = window.opener && window.opener !== window;
        if (isPopup) {
          const storedToken = localStorage.getItem('session_token');
          if (storedToken) {
            try { window.opener.postMessage({ type: 'AUTH_TOKEN', token: storedToken }, '*'); } catch (e) {}
            try { localStorage.setItem('pwa_auth_token', storedToken); } catch (e) {}
          }
          setStatus('success');
          setTimeout(() => { try { window.close(); } catch (e) {} }, 1500);
          return;
        }

        // Clear the hash/search params from URL
        if (window.history.replaceState) {
          window.history.replaceState(null, '', window.location.pathname);
        }

        // Navigate directly — no intermediate screen
        const destination = getDestination(user);
        navigate(destination, { replace: true, state: { user, fromAuth: true } });

      } catch (error) {
        console.error('[AuthCallback] Exchange failed:', error.message);
        // Iter. 411.mm21 — If we exhausted retries and it's STILL a
        // transient network error (phone wifi completely dead), tag the
        // message so the user knows it's not their account that's broken.
        const isNet = isTransientNetworkError(error);
        setErrorMessage(
          isNet
            ? 'Réseau instable. Vérifie ta connexion et appuie sur Réessayer.'
            : (error.message || 'Erreur de connexion. Réessaie.')
        );
        setRetryHint('');
        setStatus('error');
      }
    };

    processAuth();
  }, [exchangeSession, navigate, updateUser]);

  // Processing / Loading screen
  if (status === 'processing') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="auth-callback-loading">
        <div className="text-center">
          <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-5">
            <Loader2 className="w-10 h-10 text-primary animate-spin" />
          </div>
          <h2 className="font-heading text-2xl uppercase text-foreground mb-2">
            EXTRA MILES
          </h2>
          <p className="text-muted-foreground text-sm">
            {retryHint || 'Connexion en cours...'}
          </p>
          {retryHint && (
            <p className="text-[11px] text-muted-foreground/60 mt-2 max-w-xs mx-auto" data-testid="auth-retry-hint">
              Ton réseau est instable — on attend qu'il revienne.
            </p>
          )}
        </div>
      </div>
    );
  }

  // Success (only shown for PWA popup flow)
  if (status === 'success') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="auth-callback-success">
        <div className="text-center max-w-sm px-6">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-5">
            <svg className="w-10 h-10 text-green-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
          </div>
          <h2 className="font-heading text-2xl uppercase text-green-400 mb-2">Connecté</h2>
          <p className="text-muted-foreground text-sm mb-4">Retourne dans l'application EXTRA MILES.</p>
          <p className="text-xs text-muted-foreground">Cette fenêtre va se fermer automatiquement...</p>
        </div>
      </div>
    );
  }

  // Error screen
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="auth-callback-error">
      <div className="text-center max-w-sm">
        <div className="w-20 h-20 bg-destructive/20 rounded-full flex items-center justify-center mx-auto mb-5">
          <AlertCircle className="w-10 h-10 text-destructive" />
        </div>
        <h2 className="font-heading text-2xl uppercase text-foreground mb-2">
          Erreur de connexion
        </h2>
        <p className="text-muted-foreground text-sm mb-6">
          {errorMessage}
        </p>

        <div className="space-y-3">
          <Button
            onClick={() => {
              localStorage.removeItem('session_token');
              localStorage.removeItem('refresh_token');
              navigate('/', { replace: true });
            }}
            className="w-full bg-primary text-primary-foreground font-heading uppercase"
            data-testid="error-retry-btn"
          >
            Réessayer
          </Button>
          <Button
            onClick={() => {
              localStorage.clear();
              sessionStorage.clear();
              if ('caches' in window) {
                caches.keys().then(keys => keys.forEach(key => caches.delete(key)));
              }
              window.location.href = '/';
            }}
            variant="outline"
            className="w-full border-primary/50 text-primary"
            data-testid="error-clear-cache-btn"
          >
            Vider le cache et réessayer
          </Button>
        </div>

        <details className="mt-6 text-left">
          <summary className="text-xs text-muted-foreground cursor-pointer">Diagnostic</summary>
          <div className="mt-2 bg-secondary/50 rounded-lg p-3 text-[10px] text-muted-foreground font-mono break-all space-y-1">
            <p>URL: {window.location.origin}</p>
            <p>Backend: {process.env.REACT_APP_BACKEND_URL}</p>
            <p>Erreur: {errorMessage}</p>
          </div>
        </details>
      </div>
    </div>
  );
};

function getDestination(user) {
  const saved = sessionStorage.getItem('redirectAfterLogin');
  sessionStorage.removeItem('redirectAfterLogin');
  if (saved && saved !== '/') return saved;
  if (user.role === 'coach') return '/dashboard';
  if (!user.onboarding_complete) return '/onboarding';
  // Onboarded but never completed the Day-1 vocal Deep Chat
  if (user.role === 'client' && !user.deep_chat_completed) {
    return '/deep-chat';
  }
  // Onboarded but never accepted the mandatory Day-7 challenge intro
  if (user.role === 'client' && !user.welcome_intro_accepted) {
    return '/welcome-challenge-intro';
  }
  return '/dashboard';
}

export default AuthCallback;
